import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { regionAssociationService } from '../../services/regionAssociationService';
import { regionalTeamService } from '../../services/regionalTeamService';
import { regionalTeamRosterService } from '../../services/regionalTeamRosterService';
import { teamService } from '../../services/teamService';
import { playerService } from '../../services/playerService';
import { exportReportToExcel, exportReportToPDF } from '../../utils/adminReportExportUtils';
import { getRegionLabel } from '../../utils/tanzaniaRegions';

const breadcrumbItems = [
  { label: 'Dashboard', path: '/admin-dashboard' },
  { label: 'Administration' },
  { label: 'Reports' },
];

const formatStatus = (value) => (value || '—').toString().replace(/_/g, ' ');

const REPORT_DEFS = [
  {
    id: 'region-associations',
    title: 'Region Associations',
    description: 'Registered regional basketball associations and leadership contacts.',
    icon: 'MapPin',
    filename: 'region-associations',
    sheetName: 'Region Associations',
    columns: [
      { key: 'associationName', label: 'Association Name', width: 2.2 },
      { key: (r) => getRegionLabel(r.region), label: 'Region', width: 1.4 },
      { key: 'registrationNumber', label: 'Reg. No.', width: 1.2 },
      { key: (r) => formatStatus(r.registrationStatus), label: 'Status', width: 1 },
      { key: 'email', label: 'Email', width: 1.8 },
      { key: 'mobile', label: 'Mobile', width: 1.2 },
      { key: 'chairpersonName', label: 'Chairperson', width: 1.6 },
      { key: 'secretaryName', label: 'Secretary', width: 1.6 },
      { key: 'physicalAddress', label: 'Physical Address', width: 1.8 },
    ],
    load: async () => regionAssociationService.getAll(),
  },
  {
    id: 'regional-teams',
    title: 'Regional Teams',
    description: 'All regional teams with staff and registration status.',
    icon: 'Shield',
    filename: 'regional-teams',
    sheetName: 'Regional Teams',
    columns: [
      { key: 'teamName', label: 'Team Name', width: 2 },
      { key: (r) => getRegionLabel(r.region), label: 'Region', width: 1.4 },
      { key: (r) => formatStatus(r.registrationStatus), label: 'Status', width: 1 },
      { key: 'teamManager', label: 'Team Manager', width: 1.5 },
      { key: 'headCoach', label: 'Head Coach', width: 1.5 },
      { key: 'teamDoctor', label: 'Team Doctor', width: 1.4 },
      { key: 'teamStatistician', label: 'Statistician', width: 1.4 },
    ],
    load: async () => regionalTeamService.getAll(),
  },
  {
    id: 'regional-team-players',
    title: 'Regional Team Players',
    description: 'Players registered under regional teams.',
    icon: 'UsersRound',
    filename: 'regional-team-players',
    sheetName: 'Regional Players',
    columns: [
      { key: 'fullName', label: 'Player Name', width: 2 },
      { key: 'teamName', label: 'Team', width: 1.8 },
      { key: (r) => getRegionLabel(r.teamRegion), label: 'Region', width: 1.3 },
      { key: 'position', label: 'Position', width: 1.2 },
      { key: 'jerseyNumber', label: 'Jersey #', width: 0.8 },
      { key: 'age', label: 'Age', width: 0.7 },
      { key: 'email', label: 'Email', width: 1.6 },
      { key: 'phone', label: 'Phone', width: 1.2 },
    ],
    load: async () => regionalTeamRosterService.getAllPlayers(),
  },
  {
    id: 'regional-team-officials',
    title: 'Regional Team Officials',
    description: 'Officials attached to regional team rosters.',
    icon: 'UserCog',
    filename: 'regional-team-officials',
    sheetName: 'Regional Officials',
    columns: [
      { key: 'fullName', label: 'Official Name', width: 2 },
      { key: 'roleTitle', label: 'Role', width: 1.4 },
      { key: 'teamName', label: 'Team', width: 1.8 },
      { key: (r) => getRegionLabel(r.teamRegion), label: 'Region', width: 1.3 },
      { key: 'email', label: 'Email', width: 1.8 },
      { key: 'phone', label: 'Phone', width: 1.3 },
    ],
    load: async () => regionalTeamRosterService.getAllOfficials(),
  },
  {
    id: 'clubs',
    title: 'Clubs / Teams',
    description: 'League clubs registered in the system.',
    icon: 'Users',
    filename: 'clubs-teams',
    sheetName: 'Clubs',
    columns: [
      { key: 'name', label: 'Club Name', width: 2 },
      { key: 'shortName', label: 'Short Name', width: 1 },
      { key: 'city', label: 'City', width: 1.2 },
      { key: 'region', label: 'Region', width: 1.2 },
      { key: 'category', label: 'Category', width: 1 },
      { key: 'division', label: 'Division', width: 1 },
      { key: (r) => formatStatus(r.teamStatus), label: 'Status', width: 1 },
      { key: 'contactEmail', label: 'Email', width: 1.6 },
      { key: 'primaryPhone', label: 'Phone', width: 1.2 },
    ],
    load: async () => teamService.getAll(),
  },
  {
    id: 'club-players',
    title: 'Club Players',
    description: 'Licensed players registered under league clubs.',
    icon: 'User',
    filename: 'club-players',
    sheetName: 'Club Players',
    columns: [
      { key: (r) => r.userProfile?.fullName || r.name || '—', label: 'Player Name', width: 2 },
      { key: (r) => r.team?.name || '—', label: 'Club', width: 1.6 },
      { key: (r) => (r.playerPosition || r.position || '—').toString().replace(/_/g, ' '), label: 'Position', width: 1.3 },
      { key: 'jerseyNumber', label: 'Jersey #', width: 0.8 },
      { key: (r) => formatStatus(r.playerStatus || r.registrationStatus), label: 'Status', width: 1 },
      { key: (r) => r.userProfile?.email || r.email || '—', label: 'Email', width: 1.8 },
      { key: 'licenseNumber', label: 'License No.', width: 1.2 },
    ],
    load: async () => playerService.getAll(),
  },
];

const ReportsPage = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [counts, setCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [exportingId, setExportingId] = useState(null);
  const [exportFormat, setExportFormat] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const hasAccess = userProfile?.role === 'admin';

  useEffect(() => {
    if (authLoading) return;
    if (!hasAccess) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [authLoading, hasAccess, navigate]);

  const loadCounts = useCallback(async () => {
    setLoadingCounts(true);
    setError(null);
    try {
      const entries = await Promise.all(
        REPORT_DEFS.map(async (def) => {
          try {
            const rows = await def.load();
            return [def.id, Array.isArray(rows) ? rows.length : 0];
          } catch {
            return [def.id, null];
          }
        })
      );
      setCounts(Object.fromEntries(entries));
    } catch (err) {
      setError(err.message || 'Failed to load report summaries');
    } finally {
      setLoadingCounts(false);
    }
  }, []);

  useEffect(() => {
    if (hasAccess) loadCounts();
  }, [hasAccess, loadCounts]);

  const handleExport = async (def, format) => {
    setError(null);
    setSuccess(null);
    setExportingId(def.id);
    setExportFormat(format);
    try {
      const rows = await def.load();
      if (!rows?.length) {
        throw new Error(`No records found for ${def.title}`);
      }

      if (format === 'xlsx') {
        await exportReportToExcel(rows, def.columns, {
          filename: def.filename,
          sheetName: def.sheetName,
        });
      } else {
        await exportReportToPDF(rows, def.columns, {
          title: `TBF MAP — ${def.title}`,
          filename: def.filename,
        });
      }
      setSuccess(`${def.title} exported as ${format.toUpperCase()}`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.message || `Failed to export ${def.title}`);
    } finally {
      setExportingId(null);
      setExportFormat(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <Icon name="Loader2" size={40} className="text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!hasAccess) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="flex items-center justify-between mt-4 mb-6 gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="FileBarChart" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Reports</h1>
                <p className="text-muted-foreground">Export regional and league registration data as PDF or Excel</p>
              </div>
            </div>
            <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={loadCounts} loading={loadingCounts}>
              Refresh
            </Button>
          </div>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success text-sm">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {REPORT_DEFS.map((def) => {
              const count = counts[def.id];
              const isBusy = exportingId === def.id;
              return (
                <div key={def.id} className="bg-card border border-border rounded-lg card-shadow p-5 flex flex-col">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Icon name={def.icon} size={20} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-semibold text-foreground">{def.title}</h2>
                      <p className="text-sm text-muted-foreground mt-1">{def.description}</p>
                    </div>
                  </div>

                  <div className="mt-auto pt-3 border-t border-border flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-sm text-muted-foreground">
                      {loadingCounts ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Icon name="Loader2" size={14} className="animate-spin" /> Loading…
                        </span>
                      ) : count === null ? (
                        'Count unavailable'
                      ) : (
                        <>
                          <span className="font-semibold text-foreground">{count}</span> record{count === 1 ? '' : 's'}
                        </>
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="FileText"
                        iconPosition="left"
                        loading={isBusy && exportFormat === 'pdf'}
                        disabled={isBusy || loadingCounts}
                        onClick={() => handleExport(def, 'pdf')}
                      >
                        PDF
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Sheet"
                        iconPosition="left"
                        loading={isBusy && exportFormat === 'xlsx'}
                        disabled={isBusy || loadingCounts}
                        onClick={() => handleExport(def, 'xlsx')}
                      >
                        XLSX
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReportsPage;
